import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import type { Wall, FloorPlanElement, Point, TextBox } from '../types'; // Assuming types are in ../types
import type { InventoryItem, Signature, RoomPhoto } from '../types'; // Ensure these are correctly typed
import type { PropertyDetails } from '../components/PropertyDetailsForm'; // Adjust path if needed
import type { KeyEntry } from '../components/KeyEntryItem'; // Adjust path if needed

const FLOOR_PLANS_DIR = 'floorplans';

export interface SiteNotesData {
  formData: {
    address: {
      buildingName?: string;
      houseNumber: string;
      street: string;
      town: string;
      postcode: string;
    };
    propertyType: string;
    terrainType?: string; // 'urban' | 'suburban' | 'rural'
    detachment: string;
    ageBand: string;
    propertyTenure?: string;
    groundLevel: string;
    roofTypeRdSAP: string;
    wallType: string;
    partyWall: string;
    wallThickness: string;
    dryLining?: string; // 'yes' | 'no'
    roofType: string;
    roofInsulation: string;
    insulationType?: string;
    floorType: string;
    region?: string;
    assessmentPurpose?: string;
    transactionType?: string;
    inspectionDate?: string;
    processDate?: string;
    // Habitable rooms fields
    totalHabitableRooms: string;
    totalHeatedHabitableRooms: string;
    numberOfStoreys?: string;
    // Date Built fields
    dateBuiltEntries?: Array<{
      id: string;
      type: 'main' | 'extension';
      label: string;
      dateBuilt?: string;
      rirDateBuilt?: string;
    }>;
    // Wall Construction per building section
    wallConstructionSections?: Array<{
      sectionId: string;
      sectionLabel: string;
      wallType?: string;
      wallInsulationType?: string;
      wallInsulationThickness?: string;
      partyWall?: string;
      wallThickness?: string;
      dryLining?: string;
    }>;
    // Conservatory fields
    hasConservatory?: string; // 'yes' | 'no'
    conservatoryThermallySeparated?: string; // 'yes' | 'no'
    conservatoryFloorArea?: string;
    conservatoryDoubleGlazed?: string; // 'yes' | 'no'
    conservatoryGlazedPerimeter?: string;
    conservatoryRoomHeight?: string;
    conservatoryFixedHeaters?: string; // 'yes' | 'no'
    // Room in Roof toggle
    hasRoomInRoof?: string; // 'yes' | 'no' - controls whether RIR is included in PDF exports
    heatingType: string;
    heatingSystemType: string;
    heatingFuelType: string;
    heatingBrand: string;
    heatingModel: string;
    heatingSerialNumber: string;
    
    // SAP-compliant fields for legacy system
    heatingControl?: string;
    winterEfficiency?: number;
    summerEfficiency?: number;
    flowTemperature?: number;
    isCondensing?: boolean;
    isLowTemperature?: boolean;
    
    totalRads: string;
    trvs: string;
    tRad: string;
    heatingSourceLocation: string;
    tankLocation: string;
    totalWindows: string;
    glazingType: string;
    windowType: string;
    windowFrameType: string;
    glazingThickness: string;
    windowsMissingTrickleVents: string;
    missingTrickleVentsLocation: string;
    mevsKitchen: string;
    mevsBathroom: string;
    mevsEnSuite: string;
    mevsWC: string;
    mevsUtility: string;
    totalDoors: string;
    numberOfInsulatedDoors?: string;
    numberOfDraughtProofedDoors?: string;
    doorsWithoutUndercuts: string;
    doorsWithoutUndercutsLocation: string;
    // Additional ventilation fields
    openChimneys: string;
    openFlues: string;
    openChimneysFluesAttachedToClose: string;
    fluesAttachedToSolidFuelBoiler: string;
    openFluesAttachedToOtherHeater: string;
    blockedChimneys: string;
    intermittentExtractFans: string;
    passiveVents: string;
    fluelessGasFires: string;
    fixedSpaceCooling: string;
    draughtLobby: string;
    airBricks?: string;
    gmLocation: string;
    emLocations: string;
    stapLocation: string;
    additionalNotes: string;
    totalLightsX: string;
    totalLightsO: string;
    totalLEL: string;
    // Evidence photos
    evidencePhotos?: Array<{
      id: string;
      dataUrl: string;
      timestamp: string;
      description: string;
      category: string;
    }>;
    // RdSAP XML Export fields
    assessorName?: string;
    assessorAccreditationNumber?: string;
    assessorCompany?: string;
    insuranceCompany?: string;
    insurancePolicyNumber?: string;
    insuranceEffectiveDate?: string;
    insuranceExpiryDate?: string;
    insurancePILimit?: string;
    rrn?: string; // Report Reference Number
    uprn?: string; // Unique Property Reference Number
    regionCode?: string; // UK region code
    countryCode?: string; // UK country code
    xmlTransactionType?: string; // 'sale' | 'rental' | 'other'
    tenure?: string; // 'freehold' | 'leasehold' | 'other'
    reportType?: string; // 'RdSAP' | 'SAP'
    sapVersion?: string; // SAP methodology version
    calculationSoftwareName?: string;
    calculationSoftwareVersion?: string;
    schemaVersion?: string;
    pcdfRevisionNumber?: string;
    externalDefinitionsRevision?: string;
    completionDate?: string;
    registrationDate?: string;
    languageCode?: string; // 'en' | 'cy'
    // Flat-specific fields
    flatPosition?: string; // 'ground-floor' | 'mid-floor' | 'top-floor'
    flatCorridorType?: string; // 'none' | 'heated' | 'unheated-corridor' | 'unheated-stairwell'
    flatShelteredWallLength?: string;
    flatWhichFloor?: string;
    flatStoreysInBlock?: string;
    // Water heating system fields
    whsType?: string; // 'boiler-circulator' | 'community-scheme' | 'range-cooker' | 'water-heater' | 'no-hot-water-system'
    whsBoilerType?: string; // 'gas' | 'oil' | 'solid-fuel'
    whsFuelType?: string; // Various fuel options based on boiler type
    whsRangeCookerType?: string; // Range cooker sub-types
    waterHeatingCode?: string;
    waterHeatingSapCode?: string;
    hotWaterCylinderPresent?: boolean;
    cylinderSize?: string; // 'normal' | 'other'
    cylinderInsulated?: string; // 'foam' | 'other'
    cylinderInsulationThickness?: string;
    heatingsImmersionHeater?: string; // 'dual' | 'single' | 'none'
    // Water heating wizard state
    waterHeatingWizardPhase?: number; // 1-6 to track which phase user is on
    // WWHRS (Waste Water Heat Recovery System) fields
    wwhrsPresent?: string; // 'no' | 'instantaneous' | 'storage'
    totalBaths?: number;
    bathsConnectedToWwhrs?: number;
    showers?: Array<{
      id: string;
      type: string; // 'electric' | 'non-electric'
      connectedTo: string; // 'none' | 'wwhrs' | 'hot-water-cylinder' | 'direct-mains'
    }>;
    // Wall insulation fields
    wallInsulationType: string;
    wallInsulationThickness?: string;
    wallInsulationElevationsCount: string;
    wallInsulationElevations: {
      id: number;
      type: string; // front, right, left, rear
      dimensions?: {
        height: number;
        width: number;
      };
      elements?: {
        doors: Array<{
          id: string;
          height: number;
          width: number;
        }>;
        windows: Array<{
          id: string;
          height: number;
          width: number;
        }>;
        airVents: Array<{
          id: string;
        }>;
      };
      calculations?: {
        totalWallArea: number;
        totalElementsArea: number;
        treatableArea: number;
      };
      placedElements?: Array<{
        id: string;
        type: string;
        x: number;
        y: number;
        elevation: string;
        height?: number;
        width?: number;
      }>;
      wallInsulationType?: string;
    }[];
    // Global wall insulation calculations
    wallInsulationGlobalCalculations?: {
      totalDwellingArea: number;
      totalUntreatableArea: number;
      totalTreatableArea: number;
      totalWallAreaWholeHouse: number;
      poptPercentage: number;
    };
    // Room in Roof data
    roomInRoofData?: {
      measurements: Array<{
        id: string;
        label: string;
        length?: string;
        height?: string;
        insulation?: string;
        insulationType?: string;
        visible?: boolean;
        value: string;
        unit: string;
      }>;
      annotations: Array<{
        id: string;
        x: number;
        y: number;
        text: string;
      }>;
      insulationType?: string;
      ventilationType?: string;
      diagramType?: 'standard' | 'with-dormer';
      diagramImage?: string;
    };
  };
  // Store the selected PCDB heating system data for detailed PDF export
  selectedPCDBSystem?: {
    id: string;
    brand: string;
    model: string;
    model_qualifier?: string;
    type: string;
    fuel?: string;
    main_type?: string;
    output_power_kw?: number;
    sap_winter_seasonal_efficiency?: number;
    sap_summer_seasonal_efficiency?: number;
    efficiency_category?: string;
    condensing?: boolean;
    flue_type?: string;
    fan_assisted?: boolean;
    year_of_commission?: string;
    features?: string[];
    certifications?: string[];
    pcdb_reference?: string;
    boiler_id?: string;
    last_updated?: string;
    is_discontinued?: boolean;
    replacement_models?: string[];
    installation_type?: string;
  };
  // Support for multiple heating systems
  heatingSystems?: {
    id: string;
    isPrimary: boolean;
    isManualEntry?: boolean; // Track manual entry mode for each system
    isRadiatorCountManuallyOverridden?: boolean; // Track when user manually overrides radiator counts
    heatingType: string;
    heatingSystemType: string;
    heatingFuelType: string;
    heatingBrand: string;
    heatingModel: string;
    heatingSerialNumber: string;
    heatingSourceLocation: string;
    tankLocation: string;
    totalRads: string;
    trvs: string;
    tRad: string;
    
    // SAP-compliant fields
    heatingSource?: string;
    heatEmitterType?: string;
    fuelType?: string;
    heatingControl?: string;
    winterEfficiency?: number;
    summerEfficiency?: number;
    flowTemperature?: number;
    isCondensing?: boolean;
    isLowTemperature?: boolean;
    
    // PCDB system data if selected from database
    pcdbSystem?: {
      id: string;
      brand: string;
      model: string;
      model_qualifier?: string;
      type: string;
      fuel?: string;
      main_type?: string;
      output_power_kw?: number;
      sap_winter_seasonal_efficiency?: number;
      sap_summer_seasonal_efficiency?: number;
      efficiency_category?: string;
      condensing?: boolean;
      flue_type?: string;
      fan_assisted?: boolean;
      year_of_commission?: string;
      features?: string[];
      certifications?: string[];
      pcdb_reference?: string;
      boiler_id?: string;
      last_updated?: string;
      is_discontinued?: boolean;
      replacement_models?: string[];
      installation_type?: string;
    };
  }[];
  floors: {
    id: number;
    floorLevel: string;
    buildingType: string;
    height: string;
    floorArea: string;
    hlp: string;
    pw: string;
    // 🔧 HIGHLIGHTER BREAKDOWN DATA: Store detailed calculation data for accurate formulas
    highlighterBreakdown?: {
      // Rectangle dimensions for area calculation
      dimensions: {
        width: number;
        height: number;
      };
      // Individual wall segments for HLP/PW calculations
      hlpSegments: number[];
      pwSegments: number[];
      swSegments?: number[];
    };
    rooms: {
      id: number;
      name?: string; // Optional room name from floor plan (e.g., "BR1", "Kitchen")
      length: string;
      width: string;
      height: string;
      area?: number; // Room area in square meters (calculated from floor plan polygon)
      lowEnergyLights: string;
      nonLowEnergyLights: string;
      // Single radiator fields for backward compatibility (auto-sync from floor plan)
      radiatorWidth: string;
      radiatorHeight: string;
      radiatorType: string;
      // Single window fields for backward compatibility (auto-sync from floor plan)
      windowWidth: string;
      windowHeight: string;
      windowTrickleVents: string;
      // Window glazing and type fields
      windowGlazingGap?: string;
      windowGlazingType?: string;
      windowCategory?: string;
      windowOrientation?: string;
      windowDraughtProofed?: string;
      windowType?: string;
      windowSillLength?: string;
      // Arrays for additional manual radiators and windows
      radiators?: {
        id: number;
        width: string;
        height: string;
        type: string;
      }[];
      windows?: {
        id: number;
        width: string;
        height: string;
        orientation: string;
        trickleVents?: boolean;
        isDraughtProofed?: boolean;
        windowType?: 'pvc' | 'wood' | 'metal';
        glazingGap?: string;
        glazingType?: string;
        windowCategory?: string;
      }[];
    }[];
  }[];
}

export interface SavedFloorPlanData {
  floorPlans: Record<number, Wall[]>;
  currentFloor: number;
  elements: FloorPlanElement[];
  textBoxes: TextBox[];
  roomCustomLabels: Record<string, string>;
  roomLabelPositions?: Record<string, Point>;
  propertyAddress: string;
  propertyOrientation?: number;
  windowCount: { single: number; double: number };
  lightCount: { nonLed: number; led: number };
  siteNotesData?: SiteNotesData;
  // Optional saved view state
  viewOffset?: Point;
  scale?: number;
  // Metadata
  createdAt: string;
  updatedAt: string;
  name: string;
  version: string;
  // Migration-specific properties
  migrationLog?: string[];
  windowPreservationData?: {
    originalWindowCount: number;
    preservedAt: string;
    preservedWindows: Array<{
      id: string;
      floor: number;
      wallId: string;
      position: Point;
      dimensions: { width: number; height: number };
    }>;
  };
}

// Address utility functions
export const combineAddressFields = (address: {
  buildingName?: string;
  houseNumber: string;
  street: string;
  town: string;
  postcode: string;
}): string => {
  // Combine house number with street on the same line
  const streetLine = address.houseNumber?.trim() && address.street?.trim()
    ? `${address.houseNumber.trim()} ${address.street.trim()}`
    : (address.street?.trim() || address.houseNumber?.trim() || '');
  
  const parts = [
    address.buildingName?.trim(), // Include building/flat name if provided
    streetLine,
    address.town?.trim(),
    address.postcode?.trim()
  ].filter(part => part && part.length > 0);
  
  return parts.join(', ');
};

export const combineAddressForFileName = (address: {
  buildingName?: string;
  houseNumber: string;
  street: string;
  town: string;
  postcode: string;
}): string => {
  // Combine house number with street on the same line
  const streetLine = address.houseNumber?.trim() && address.street?.trim()
    ? `${address.houseNumber.trim()} ${address.street.trim()}`
    : (address.street?.trim() || address.houseNumber?.trim() || '');
  
  const parts = [
    address.buildingName?.trim(), // 🔧 FIX: Include building/flat name in filename
    streetLine,
    address.town?.trim(),
    address.postcode?.trim()
  ].filter(part => part && part.length > 0);
  
  return parts.join(' ').trim().replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_').substring(0, 50);
};

export const parseAddressFromString = (addressString: string): {
  buildingName?: string;
  houseNumber: string;
  street: string;
  town: string;
  postcode: string;
} => {
  const parts = addressString.split(',').map(part => part.trim());
  
  // Check if first part looks like a building/flat name
  const buildingNamePattern = /^(Flat|Apartment|Unit|Suite|Building)\s+/i;
  if (parts.length >= 4 && buildingNamePattern.test(parts[0])) {
    // First part is building name, second might have house number + street combined
    const streetPart = parts[1] || '';
    const streetMatch = streetPart.match(/^(\d+[a-zA-Z]?)\s+(.+)$/);
    
    if (streetMatch) {
      return {
        buildingName: parts[0],
        houseNumber: streetMatch[1],
        street: streetMatch[2],
        town: parts[2] || '',
        postcode: parts[3] || ''
      };
    }
    
    return {
      buildingName: parts[0],
      houseNumber: '',
      street: parts[1] || '',
      town: parts[2] || '',
      postcode: parts[3] || ''
    };
  }
  
  // Standard parsing - first part might have house number + street combined
  if (parts.length >= 1) {
    const firstPart = parts[0] || '';
    const streetMatch = firstPart.match(/^(\d+[a-zA-Z]?)\s+(.+)$/);
    
    if (streetMatch) {
      return {
        houseNumber: streetMatch[1],
        street: streetMatch[2],
        town: parts[1] || '',
        postcode: parts[2] || ''
      };
    }
  }
  
  return {
    houseNumber: parts[0] || '',
    street: parts[1] || '',
    town: parts[2] || '',
    postcode: parts[3] || ''
  };
};

export const loadFloorPlanFromFile = async (fileName: string): Promise<SavedFloorPlanData | null> => {
  const filePath = `${FLOOR_PLANS_DIR}/${fileName}`;
  console.log(`[fileUtils] Attempting to load from: ${filePath}`);
  
  // 🔄 Reset room ID system for this file so room numbering starts at room_1
  const { resetRoomIdSystem } = await import('./floorPlanUtils');
  resetRoomIdSystem();
  try {
    const readFileResult = await Filesystem.readFile({
      path: filePath,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });

    if (typeof readFileResult.data === 'string') {
      const parsedData: SavedFloorPlanData = JSON.parse(readFileResult.data);
      console.log(`[fileUtils] Successfully parsed data for ${fileName}:`, parsedData);
      return parsedData;
    } else {
      // Should not happen with Encoding.UTF8 if successful, but good to check
      console.error(`[fileUtils] Read file data is not a string for ${fileName}.`);
      return null;
    }
  } catch (error) {
    console.error(`[fileUtils] Error loading or parsing floor plan ${fileName}:`, error);
    // Check if the error is because the file doesn't exist
    if ((error as any).message && (error as any).message.includes('File does not exist')) {
      console.warn(`[fileUtils] File ${fileName} not found.`);
    }
    return null;
  }
};

// You can also move the save logic here in the future if desired
// For now, we'll keep it in FloorPlanCreator as it's tightly coupled with its state. 

// Assuming Room structure for inventory is defined in PropertyInventory or types.ts
// For now, let's define a placeholder here if not already globally available.
// It should match the structure used in PropertyInventory.tsx's `rooms` state.
interface InventoryRoom {
  name: string;
  items: InventoryItem[];
  signatures?: Array<{ // For room-specific signatures, if any
    type: 'tenant' | 'landlord';
    dataURL: string;
  }>;
  // Mid-term report fields
  rating?: 1 | 2 | 3 | 4 | 5;
  cleanliness?: 'professionally cleaned' | 'good domestic standard' | 'average domestic standard' | 'poor domestic standard' | 'not cleaned' | '';
}

export interface SavedPropertyInventoryData {
  reportType: 'checkIn' | 'checkOut' | 'midTerm';
  propertyDetails: PropertyDetails;
  rooms: InventoryRoom[]; // This is crucial, holds items with photos
  certificates: {
    gas: { date: string; number: string };
    electrical: { date: string; number: string };
  };
  meterReadings: {
    gas: { reading: string; location: string; supplier: string; serialNo: string };
    electricity: { reading: string; location: string; supplier: string; serialNo: string };
    water: { reading: string; location: string; supplier: string; serialNo: string };
  };
  keysData: KeyEntry[];
  signatures: Signature; // Top-level signatures (assessor, customer)
  // Metadata
  createdAt: string;
  updatedAt: string;
  name: string; // Filename or user-defined name
  version: string;
}

const INVENTORIES_DIR = 'inventories'; // Dedicated directory for inventories

export const savePropertyInventoryToFile = async (
  fileName: string,
  data: SavedPropertyInventoryData
): Promise<void> => {
  const filePath = `${INVENTORIES_DIR}/${fileName}`;
  console.log(`[fileUtils] Attempting to save inventory to: ${filePath}`);
  try {
    // Ensure the directory exists
    try {
      await Filesystem.readdir({
        path: INVENTORIES_DIR,
        directory: Directory.Data,
      });
    } catch (e) {
      // Directory does not exist, create it
      console.log(`[fileUtils] Directory ${INVENTORIES_DIR} does not exist. Creating it.`);
      await Filesystem.mkdir({
        path: INVENTORIES_DIR,
        directory: Directory.Data,
        recursive: false, // true if it might be nested, false for a single directory
      });
    }

    await Filesystem.writeFile({
      path: filePath,
      data: JSON.stringify(data, null, 2), // Pretty print JSON
      directory: Directory.Data,
      encoding: Encoding.UTF8,
      recursive: true, // This ensures intermediate directories are created if INVENTORIES_DIR contained subdirectories (though not in this case)
    });
    console.log(`[fileUtils] Inventory ${fileName} saved successfully.`);
  } catch (error) {
    console.error(`[fileUtils] Error saving inventory ${fileName}:`, error);
    throw error; // Re-throw to be caught by the caller
  }
};

export const loadPropertyInventoryFromFile = async (fileName: string): Promise<SavedPropertyInventoryData | null> => {
  const filePath = `${INVENTORIES_DIR}/${fileName}`;
  console.log(`[fileUtils] Attempting to load inventory from: ${filePath}`);
  try {
    // Ensure the directory exists (optional, read will fail if not)
    // await Filesystem.mkdir({ path: INVENTORIES_DIR, directory: Directory.Data, recursive: true });

    const readFileResult = await Filesystem.readFile({
      path: filePath,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });

    if (typeof readFileResult.data === 'string') {
      const parsedData: SavedPropertyInventoryData = JSON.parse(readFileResult.data);
      console.log(`[fileUtils] Successfully parsed inventory data for ${fileName}:`, parsedData);
      return parsedData;
    } else {
      console.error(`[fileUtils] Read inventory file data is not a string for ${fileName}.`);
      return null;
    }
  } catch (error) {
    console.error(`[fileUtils] Error loading or parsing inventory ${fileName}:`, error);
    if ((error as any).message && (error as any).message.includes('File does not exist')) {
      console.warn(`[fileUtils] Inventory file ${fileName} not found.`);
    }
    return null;
  }
};